// src/modules/company/pages/CompanyDetails.tsx
import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import {
    createCompany,
    getCompanyById,
    updateCompany,
} from "../api/companyService";
import "../styles/companyDetails.css";
import { toast } from "react-toastify";
/* =====================================

   ⚠️ DEV ONLY – REMOVE LATER
===================================== */
const HARDCODED_COMPANY_ID = 2;

interface CompanyForm {
    id?: number;
    company_name: string;
    company_type: string;
    email: string;
    phone_no: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    website: string;
}

const CompanyDetails: React.FC = () => {
    const [formData, setFormData] = useState<CompanyForm>({
        company_name: "",
        company_type: "",
        email: "",
        phone_no: "",
        address: "",
        address2: "",
        city: "",
        state: "",
        website: "",
    });

    const [initialData, setInitialData] = useState<CompanyForm | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    /* =========================
       HELPERS
    ========================= */
    const isEmpty = (v: any) => v === null || v === undefined || v === "";
    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValidPhone = (phone: string) =>
        /^[6-9]\d{9}$/.test(phone);

    /* =========================
       FETCH COMPANY
    ========================= */
    useEffect(() => {
        fetchCompany();
    }, []);

    const fetchCompany = async () => {
        try {
            const res = await getCompanyById(HARDCODED_COMPANY_ID);
            const company = res.data.data;

            const data: CompanyForm = {
                id: company.id,
                company_name: company.company_name || "",
                company_type: company.company_type || "",
                email: company.email || "",
                phone_no: company.phone_no || "",
                address: company.address || "",
                address2: company.address2 || "",
                city: company.city || "",
                state: company.state || "",
                website: company.website || "",
            };

            setFormData(data);
            setInitialData(data);
            setIsEditMode(false); // VIEW MODE
        } catch {
            // No company → CREATE MODE
            setIsEditMode(true);
        }
    };

    /* =========================
       VALIDATION
    ========================= */
    const validate = () => {
        const e: Record<string, string> = {};

        if (isEmpty(formData.company_name))
            e.company_name = "Company name is required";

        if (isEmpty(formData.email)) {
            e.email = "Email is required";
        } else if (!isValidEmail(formData.email)) {
            e.email = "Invalid email address";
        }

        if (isEmpty(formData.phone_no)) {
            e.phone_no = "Phone number is required";
        } else if (!isValidPhone(formData.phone_no)) {
            e.phone_no = "Enter valid 10-digit mobile number";
        }

        if (isEmpty(formData.address))
            e.address = "Address is required";

        if (isEmpty(formData.state))
            e.state = "State is required";

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* =========================
       HANDLERS
    ========================= */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleCancel = () => {
        if (initialData) {
            setFormData(initialData);
        }
        setErrors({});
        setIsEditMode(false);
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSubmitting(true);

        const payload = {
            company_name: formData.company_name,
            company_type: formData.company_type,
            email: formData.email,
            phone_no: formData.phone_no,
            address: formData.address,
            address2: formData.address2,
            city: formData.city,
            state: formData.state,
            website: formData.website,
        };

        try {
            if (formData.id) {
                await updateCompany(formData.id, payload);
                toast.success("Company details updated successfully");
            } else {
                await createCompany(payload);
                toast.success("Company details created successfully");
            }

            fetchCompany(); // reload & lock view mode
        } catch (err: any) {
    console.error(err);

    const apiErrors = err?.response?.data;

    if (apiErrors && typeof apiErrors === "object") {
        const fieldErrors: Record<string, string> = {};

        // Backend → UI field mapping (VERY IMPORTANT)
        if (apiErrors.company_name?.length)
            fieldErrors.company_name = apiErrors.company_name[0];

        if (apiErrors.email?.length)
            fieldErrors.email = apiErrors.email[0];

        if (apiErrors.phone_no?.length)
            fieldErrors.phone_no = apiErrors.phone_no[0];

        if (apiErrors.address?.length)
            fieldErrors.address = apiErrors.address[0];

        if (apiErrors.state?.length)
            fieldErrors.state = apiErrors.state[0];

        if (apiErrors.website?.length)
            fieldErrors.website = apiErrors.website[0];

        setErrors(fieldErrors);
        toast.error("Please fix the highlighted errors");
        return;
    }

    toast.error("Failed to save company details");
} finally {
            setSubmitting(false);
        }

    };

    /* =========================
       UI
    ========================= */
    return (
        <>
            <PageHeader
                title="Company Details"
                breadcrumbs={[
                    { label: "Home", path: "/dashboard" },
                    { label: "Company Details" },
                ]}

            />

            <div className="bg-gray">
                <div className="container pt-4 pb-4">
                    <div className="card p-4 pt-5">
                        {!isEditMode && (
                            <button
                                type="button"
                                className="btn btn-primary edit-icon-btn"
                                title="Edit Company"
                                onClick={() => setIsEditMode(true)}
                            >
                                <i className="fas fa-edit text-white"></i>
                            </button>
                        )}
                        <div className="row g-4 pt-3">

                            {/* Company Name */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        className={`form-control ${errors.company_name ? "is-invalid" : ""}`}
                                        name="company_name"
                                        placeholder="Company Name"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Company Name *</label>
                                    {errors.company_name && (
                                        <div className="invalid-feedback">{errors.company_name}</div>
                                    )}
                                </div>
                            </div>

                            {/* Company Type */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        name="company_type"
                                        placeholder="Company Type"
                                        value={formData.company_type}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Company Type</label>
                                </div>
                            </div>

                            {/* Phone */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        className={`form-control ${errors.phone_no ? "is-invalid" : ""}`}
                                        name="phone_no"
                                        placeholder="Phone"
                                        value={formData.phone_no}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Phone *</label>
                                    {errors.phone_no && (
                                        <div className="invalid-feedback">{errors.phone_no}</div>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                        name="email"
                                        placeholder="Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Email *</label>
                                    {errors.email && (
                                        <div className="invalid-feedback">{errors.email}</div>
                                    )}
                                </div>
                            </div>

                            {/* Address */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        className={`form-control ${errors.address ? "is-invalid" : ""}`}
                                        name="address"
                                        placeholder="Address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Address *</label>
                                    {errors.address && (
                                        <div className="invalid-feedback">{errors.address}</div>
                                    )}
                                </div>
                            </div>

                            {/* Address 2 */}
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        name="address2"
                                        placeholder="Address 2"
                                        value={formData.address2}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Address 2</label>
                                </div>
                            </div>

                            {/* City */}
                            <div className="col-md-3">
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        name="city"
                                        placeholder="City"
                                        value={formData.city}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>City</label>
                                </div>
                            </div>

                            {/* State */}
                            <div className="col-md-3">
                                <div className="form-floating">
                                    <input
                                        className={`form-control ${errors.state ? "is-invalid" : ""}`}
                                        name="state"
                                        placeholder="State"
                                        value={formData.state}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>State *</label>
                                    {errors.state && (
                                        <div className="invalid-feedback">{errors.state}</div>
                                    )}
                                </div>
                            </div>

                            {/* Website */}
                            <div className="col-md-3">
                                <div className="form-floating">
                                    <input
                                       className={`form-control ${errors.website ? "is-invalid" : ""}`}
                                        name="website"
                                        placeholder="Website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        disabled={!isEditMode}
                                    />
                                    <label>Website</label>
                                     {errors.website && (
                                        <div className="invalid-feedback">{errors.website}</div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* FOOTER */}
                        {isEditMode && (
                            <div className="d-flex justify-content-end gap-2 mt-4 pt-4 btn-footer">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving..." : "Save"}
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </>
    );
};

export default CompanyDetails;
